/**
 * Patient Detail Page
 * Detailed view of a single patient with tabbed interface
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  AlertCircle,
  CheckCircle,
  FileText,
  X,
} from 'lucide-react';
import {
  usePatientQuery,
  usePatientAppointmentsQuery,
  usePatientTreatmentsQuery,
  usePatientInvoicesQuery,
  usePatientImagesQuery,
  useDeletePatient,
} from '../hooks/usePatients';
import { TreatmentHistory } from '../components/patients/TreatmentHistory';
import { AppointmentHistory } from '../components/patients/AppointmentHistory';
import { InvoiceHistory } from '../components/patients/InvoiceHistory';
import { ImageGallery } from '../components/patients/ImageGallery';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { formatKennitala, formatDate, calculateAge } from '../lib/utils';

type Tab = 'info' | 'treatments' | 'appointments' | 'invoices' | 'images';

export function PatientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { provider } = useAuth();

  const patientId = parseInt(id!);

  // State
  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Queries
  const { data: patient, isLoading: patientLoading } = usePatientQuery(patientId);
  const { data: appointments, isLoading: appointmentsLoading } =
    usePatientAppointmentsQuery(patientId);
  const { data: treatments, isLoading: treatmentsLoading } =
    usePatientTreatmentsQuery(patientId);
  const { data: invoices, isLoading: invoicesLoading } =
    usePatientInvoicesQuery(patientId);
  const { data: images, isLoading: imagesLoading } = usePatientImagesQuery(patientId);

  // Mutations
  const deletePatient = useDeletePatient();

  const handleDelete = async () => {
    try {
      await deletePatient.mutateAsync(patientId);
      navigate('/patients');
    } catch (error) {
      console.error('Failed to delete patient:', error);
    }
  };

  if (patientLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner text="Hleð sjúklingi..." size="lg" />
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Sjúklingur fannst ekki</h2>
        <button
          onClick={() => navigate('/patients')}
          className="mt-4 text-brand-primary hover:underline"
        >
          Fara til baka
        </button>
      </div>
    );
  }

  const fullName = `${patient.first_name} ${patient.last_name}`;
  const age = patient.date_of_birth ? calculateAge(patient.date_of_birth!) : null;

  const tabs = [
    { id: 'info' as Tab, label: 'Upplýsingar', icon: User },
    { id: 'treatments' as Tab, label: 'Meðferðir', icon: FileText },
    { id: 'appointments' as Tab, label: 'Tímapantanir', icon: Calendar },
    { id: 'invoices' as Tab, label: 'Reikningar', icon: FileText },
    { id: 'images' as Tab, label: 'Myndir', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/patients')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{fullName}</h1>
            <p className="text-gray-600">
              {patient.kennitala && formatKennitala(patient.kennitala!)}
              {age !== null && ` · ${age} ára`}
            </p>
          </div>
        </div>

        {provider?.permissions.edit && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/patients/${patientId}/edit`)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-5 h-5" />
              Breyta
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 border border-error text-error rounded-lg hover:bg-error/10 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              Eyða
            </button>
          </div>
        )}
      </div>

      {/* Patient Info Card */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Tengiliðir</h3>
            <div className="space-y-2 text-sm">
              {patient.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{patient.phone}</span>
                </div>
              )}
              {patient.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span>{patient.email}</span>
                </div>
              )}
              {patient.address && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {patient.address}
                    {patient.city && `, ${patient.city}`}
                    {patient.postal_code && ` ${patient.postal_code}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Medical Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Heilsufar</h3>
            <div className="space-y-2 text-sm">
              {patient.allergies && (
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-error">Ofnæmi</p>
                    <p className="text-gray-600">{patient.allergies}</p>
                  </div>
                </div>
              )}
              {patient.medical_conditions && (
                <div className="text-gray-600">
                  <p className="font-medium">Sjúkdómar</p>
                  <p>{patient.medical_conditions}</p>
                </div>
              )}
              {patient.current_medications && (
                <div className="text-gray-600">
                  <p className="font-medium">Lyf</p>
                  <p>{patient.current_medications}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Staða</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {patient.myhealth_user_id ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-success" />
                    <span className="text-sm text-gray-600">MyHealth tengdur</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">MyHealth ótengdur</span>
                  </>
                )}
              </div>
              {patient.last_visit_date && (
                <div className="text-sm text-gray-600">
                  <p className="font-medium">Síðasta heimsókn</p>
                  <p>{formatDate(patient.last_visit_date)}</p>
                </div>
              )}
              {patient.emergency_contact_name && (
                <div className="text-sm text-gray-600">
                  <p className="font-medium">Neyðartengiliður</p>
                  <p>{patient.emergency_contact_name}</p>
                  {patient.emergency_contact_phone && <p>{patient.emergency_contact_phone}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {/* Tab Headers */}
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Persónuupplýsingar</h3>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-600">Fullt nafn</dt>
                      <dd className="font-medium">{fullName}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Kennitala</dt>
                      <dd className="font-mono font-medium">
                        {patient.kennitala ? formatKennitala(patient.kennitala!) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Fæðingardagur</dt>
                      <dd className="font-medium">
                        {patient.date_of_birth ? formatDate(patient.date_of_birth!) : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Tungumál</dt>
                      <dd className="font-medium">
                        {patient.preferred_language === 'is' ? 'Íslenska' : 'English'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {patient.insurance_provider && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Tryggingar</h3>
                    <dl className="space-y-2 text-sm">
                      <div>
                        <dt className="text-gray-600">Tryggingafélag</dt>
                        <dd className="font-medium">{patient.insurance_provider}</dd>
                      </div>
                      {patient.insurance_number && (
                        <div>
                          <dt className="text-gray-600">Trygginganúmer</dt>
                          <dd className="font-medium">{patient.insurance_number}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>

              {patient.notes && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Athugasemdir</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                    {patient.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'treatments' && (
            <TreatmentHistory treatments={treatments} isLoading={treatmentsLoading} />
          )}

          {activeTab === 'appointments' && (
            <AppointmentHistory appointments={appointments} isLoading={appointmentsLoading} />
          )}

          {activeTab === 'invoices' && (
            <InvoiceHistory invoices={invoices} isLoading={invoicesLoading} />
          )}

          {activeTab === 'images' && (
            <ImageGallery images={images} isLoading={imagesLoading} />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-strong p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Staðfesta eyðingu</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Ertu viss um að þú viljir eyða sjúklingnum <strong>{fullName}</strong>? Þessi
              aðgerð er ekki afturkræf.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletePatient.isPending}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Hætta við
              </button>
              <button
                onClick={handleDelete}
                disabled={deletePatient.isPending}
                className="px-4 py-2 bg-error text-white rounded-lg hover:bg-error/90 transition-colors disabled:opacity-50"
              >
                {deletePatient.isPending ? 'Eyði...' : 'Eyða sjúklingi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
